"use client"

import { createContext, useCallback, useContext, useEffect, useState } from "react"
import type { ReactNode } from "react"

import { getFirebaseAuth, getGoogleProvider } from "@/lib/firebase-client"

export interface AuthUser {
  id: string
  name: string | null
  email: string | null
  photoURL: string | null
  isAdmin: boolean
  isEditor: boolean
  isSubscriber: boolean
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  isAdmin: boolean
  isEditor: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Exchange a Firebase ID token for a ModulifyDB-backed session cookie.
  const establish = useCallback(async (fbUser: any): Promise<AuthUser | null> => {
    const token = await fbUser.getIdToken()
    const res = await fetch("/api/auth/session", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    })
    if (res.ok) {
      const data = await res.json()
      setUser(data.user ?? null)
      return data.user ?? null
    }
    setUser(null)
    return null
  }, [])

  useEffect(() => {
    let unsubscribe = () => {}
    let active = true

    ;(async () => {
      const auth = getFirebaseAuth()
      if (!auth) {
        // Firebase web config not provided yet; treat as signed out.
        if (active) setLoading(false)
        return
      }
      const { onAuthStateChanged } = await import("firebase/auth")
      unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
        if (fbUser) {
          try {
            await establish(fbUser)
          } catch {
            setUser(null)
          }
        } else {
          setUser(null)
        }
        if (active) setLoading(false)
      })
    })()

    return () => {
      active = false
      unsubscribe()
    }
  }, [establish])

  const signInWithGoogle = useCallback(async () => {
    const auth = getFirebaseAuth()
    if (!auth) {
      throw new Error("Sign-in is not configured yet.")
    }
    const { signInWithPopup } = await import("firebase/auth")
    const result = await signInWithPopup(auth, getGoogleProvider())
    await establish(result.user)
  }, [establish])

  const signOut = useCallback(async () => {
    const auth = getFirebaseAuth()
    try {
      if (auth) {
        const { signOut: firebaseSignOut } = await import("firebase/auth")
        await firebaseSignOut(auth)
      }
    } catch {
      // ignore
    }
    try {
      await fetch("/api/auth/logout", { method: "POST" })
    } catch {
      // ignore
    }
    setUser(null)
    if (typeof window !== "undefined") {
      window.location.href = "/"
    }
  }, [])

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me", { cache: "no-store" })
      if (res.ok) {
        const data = await res.json()
        setUser(data.user ?? null)
      }
    } catch {
      // ignore
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin: !!user?.isAdmin,
        isEditor: !!user?.isEditor,
        signInWithGoogle,
        signOut,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return ctx
}
